import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, Loader2, ClipboardList, Receipt, CalendarClock, ShieldCheck, Wrench, Mail,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { PowerBuiltMark } from "@/components/PowerBuiltMark";

const BENEFITS = [
  { icon: ClipboardList, label: "Track your projects" },
  { icon: Receipt,       label: "View invoices and payments" },
  { icon: CalendarClock, label: "Schedule appointments" },
  { icon: ShieldCheck,   label: "Access warranties and documents" },
  { icon: Wrench,        label: "Request maintenance and upgrades" },
];

// Route a freshly-authenticated user to the right landing page.
// New sign-ins whose profile is incomplete land on /profile so they can add
// their real name before touching any core workflow.
function landingFor(user) {
  if (user.role === "admin" || user.role === "super_admin") return "/admin";
  if (user.role === "employee") return "/employee";
  return user.profile_complete === false ? "/profile?complete=1" : "/dashboard";
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { setAuthFromResponse } = useAuth();
  const [emailMode, setEmailMode] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleFacebookLogin = () => {
    // Facebook OAuth requires a Facebook Developer app + app_id/secret + verified
    // domain in the FB dashboard. Not yet wired — flag to the owner rather than
    // fake a working flow.
    toast.info("Facebook sign-in is coming soon. Please use Google or email in the meantime.");
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      // Industry-standard: email only. We create or find the account; the
      // user completes the rest of their profile after they land.
      const r = await api.post("/auth/dev-login", { email: email.trim(), role: "client" });
      setAuthFromResponse(r.data);
      navigate(landingFor(r.data.user));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign in failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white">
      {/* Left — sign-in form */}
      <div className="flex flex-col justify-center px-8 md:px-16 py-12 bg-white order-2 md:order-1">
        <Link to="/" className="flex items-center gap-3 mb-12" data-testid="login-logo">
          <PowerBuiltMark className="w-11 h-11" darkOnLight />
          <div className="leading-tight">
            <div className="font-heading font-semibold tracking-wide text-[15px] text-[#0A2540]">
              PowerBuilt
            </div>
            <div className="text-[#94A3B8] text-[10px] tracking-[0.28em] uppercase">
              Solar · Electrical · Maintenance
            </div>
          </div>
        </Link>

        <div className="max-w-md">
          <h1 className="text-4xl font-heading font-medium text-[#0A2540] tracking-tight">Welcome back</h1>
          <p className="mt-3 text-[#475569]">
            Sign in to your PowerBuilt account.
          </p>

          <div className="mt-10 space-y-3">
            <button
              onClick={handleGoogleLogin}
              data-testid="google-signin-btn"
              className="w-full flex items-center justify-center gap-3 border-2 border-[#E2E8F0] hover:border-[#0A2540] rounded-md py-3.5 transition-all bg-white text-[#0A2540] font-medium"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            <button
              onClick={handleFacebookLogin}
              data-testid="facebook-signin-btn"
              className="w-full flex items-center justify-center gap-3 border-2 border-[#E2E8F0] hover:border-[#0A2540] rounded-md py-3.5 transition-all bg-white text-[#0A2540] font-medium"
              title="Coming soon"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#1877F2">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              Continue with Facebook
            </button>

            <div className="relative py-1">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#E2E8F0]"></div></div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-white px-3 text-[#475569] uppercase tracking-wider">or</span>
              </div>
            </div>

            {!emailMode ? (
              <button
                onClick={() => setEmailMode(true)}
                className="w-full flex items-center justify-center gap-3 border-2 border-[#E2E8F0] hover:border-[#0A2540] rounded-md py-3.5 transition-all bg-white text-[#0A2540] font-medium"
                data-testid="toggle-email-signin"
              >
                <Mail className="w-5 h-5 text-[#475569]" />
                Continue with Email
              </button>
            ) : (
              <form onSubmit={handleEmailLogin} className="space-y-3" data-testid="email-signin-form">
                <label className="block">
                  <span className="text-xs text-[#475569] font-medium mb-1 block">Email address</span>
                  <input
                    type="email"
                    required
                    autoFocus
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="email-input"
                    className="w-full border border-[#E2E8F0] rounded-md px-4 py-3 text-sm focus:ring-2 focus:ring-[#C9A356]/50 focus:border-[#C9A356] outline-none"
                  />
                </label>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="email-signin-submit"
                  className="btn-accent w-full"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ArrowRight className="w-4 h-4 ml-2" /></>}
                </button>
                <button
                  type="button"
                  onClick={() => setEmailMode(false)}
                  className="w-full text-xs text-[#475569] hover:text-[#0A2540] transition py-1"
                >
                  ← Use another method
                </button>
                <p className="text-[11px] text-[#94A3B8] text-center leading-relaxed">
                  New here? We'll create your account and ask for a couple of details next.
                </p>
              </form>
            )}
          </div>

          <p className="mt-8 text-sm text-[#475569]">
            New to PowerBuilt?{" "}
            <Link to="/register" className="font-medium text-[#0A2540] underline decoration-[#C9A356] decoration-2 underline-offset-4 hover:text-[#C9A356]" data-testid="link-register">
              Create your account
            </Link>
          </p>

          <p className="mt-6 text-xs text-[#94A3B8] leading-relaxed">
            By signing in, you agree to our Terms of Service and Privacy Policy.
          </p>
        </div>
      </div>

      {/* Right — brand + benefits (simplified) */}
      <div className="hidden md:flex relative bg-[#0A2540] overflow-hidden order-1 md:order-2">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A2540] via-[#103960] to-[#0A2540]"></div>
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 20% 30%, rgba(201,163,86,0.18) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(201,163,86,0.15) 0%, transparent 50%)`,
          }}
        />
        <div className="relative h-full flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <PowerBuiltMark className="w-12 h-12" />
            <div className="leading-tight">
              <div className="font-heading font-semibold tracking-wide text-[15px] text-white">
                PowerBuilt
              </div>
              <div className="text-white/50 text-[10px] tracking-[0.28em] uppercase">
                Solar · Electrical · Maintenance
              </div>
            </div>
          </div>

          <div className="max-w-md">
            <p className="overline text-[#C9A356] mb-4">Your one-stop digital workspace for solar, electrical &amp; maintenance</p>
            <h2 className="font-heading text-4xl font-medium leading-tight">
              Everything PowerBuilt.<br />
              <span className="text-[#C9A356]">One account.</span>
            </h2>
            <p className="mt-5 text-slate-300 leading-relaxed">
              From your first consultation through every service call — manage the whole relationship in a single, private workspace.
            </p>

            <ul className="mt-8 space-y-3.5" data-testid="login-benefits">
              {BENEFITS.map((b) => (
                <li key={b.label} className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-lg bg-[#C9A356]/15 grid place-items-center text-[#C9A356] shrink-0">
                    <b.icon className="w-4 h-4" />
                  </span>
                  <span className="text-sm text-white/90">{b.label}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="text-xs text-white/40">
            Customer • Employee • Administrator — one sign in, the right dashboard.
          </div>
        </div>
      </div>
    </div>
  );
}
